import type { MessageAuthor, Named, Presence, PublicUser, SelfUser } from "@shared/api";

export interface Person {
  id: string;
  name: string;
  handle: string;
  avatarId: string | null;
  /** Украшение поверх аватара, как в Discord; нет — просто аватар. */
  decorationId?: string | null;
  presence: Presence | null;
  online: boolean;
}

export function fromPublic(user: PublicUser): Person {
  return {
    id: user.id,
    name: user.display_name || user.username,
    handle: user.handle,
    avatarId: user.avatar_id,
    decorationId: user.decoration_id ?? null,
    presence: user.status_presence,
    online: user.online,
  };
}

export function fromSelf(user: SelfUser): Person {
  return {
    id: user.id,
    name: user.display_name || user.username,
    handle: user.handle,
    avatarId: user.avatar_id,
    decorationId: user.decoration_id ?? null,
    presence: user.status_presence,
    online: user.online,
  };
}

export function fromAuthor(author: MessageAuthor): Person {
  return {
    id: author.id,
    name: author.display_name || author.username,
    handle: author.handle,
    avatarId: author.avatar_id,
    decorationId: author.decoration_id ?? null,
    presence: null,
    online: false,
  };
}

/**
 * Автор строки ленты — по свежему профилю, если он у нас есть.
 *
 * В сообщении лежит снимок автора, набранный при отправке, и меняться сам он
 * не умеет: сменивший аватар оставался в ленте прежним до перезагрузки
 * страницы, хотя в списке друзей и в составе сервера уже сменился. Событие
 * `user` обновляет как раз те списки, из которых собран `people`, — отсюда и
 * берётся живое значение.
 *
 * Свой профиль отдельно: в переписке себя нет ни в друзьях, ни в составе, а
 * своя-то смена аватара заметнее всего.
 *
 * Снимок остаётся запасным, и это не временная мера: автор может быть ни
 * другом, ни соседом по серверу — вышедший с сервера человек в ленте
 * остаётся, а в списках его уже нет.
 */
export function livePerson(
  author: MessageAuthor,
  people: Map<string, Named>,
  me: Person | null,
): Person {
  if (me && me.id === author.id) return me;

  const known = people.get(author.id);
  if (!known) return fromAuthor(author);

  return {
    id: known.id,
    name: known.display_name || known.username,
    handle: known.handle,
    avatarId: known.avatar_id,
    decorationId: known.decoration_id ?? null,
    // Присутствие в ленте не рисуется: у плашки строки `mark` всегда пуст.
    presence: null,
    online: false,
  };
}

/** Метка присутствия: какую форму рисовать. */
export type MarkKind = "online" | "away" | "focus" | "dnd" | "hidden" | "offline";

export const PRESENCE_LABELS: Record<Presence, string> = {
  online: "в сети",
  idle: "отошёл",
  focus: "сосредоточен",
  busy: "не беспокоить",
  invisible: "невидимка",
};

export const PRESENCE_ORDER: Presence[] = ["online", "idle", "focus", "busy", "invisible"];

export function markOf(presence: Presence | null): MarkKind {
  switch (presence) {
    case "online":
      return "online";
    case "idle":
      return "away";
    case "focus":
      return "focus";
    case "busy":
      return "dnd";
    case "invisible":
      return "hidden";
    default:
      return "offline";
  }
}

/**
 * Метка человека в чужих глазах.
 *
 * Сервер для невидимки отдаёт `online: false` и прячет режим, так что
 * невидимка здесь и не появится — вопрос только в том, что показать, когда
 * присутствие есть, но режим не выставлен.
 */
export function markFor(person: Person): MarkKind {
  if (!person.online) return "offline";
  return markOf(person.presence ?? "online");
}
